import Notification from '../../core/Notification';
import Locked from '../../core/Locked';
import { authorisationService } from '../../core/services/security/authorisationService';
import ProjectProvider from '../../core/providers/ProjectProvider';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import GS from '../../grafsync/grafsync';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import { getGraphFromProject, makeCancelable, sortObjects } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import { withRouter } from 'react-router-dom';
import VersionWarning from '../../core/VersionWarning';

// This component is used to wrap components that need informatIon about the taxonomies, collection or associated projects for a project
// We do this low down the component tree to avoid unwanted UI updates causes by update events

const loadOptions = {
  predicates: [
    "http://purl.org/dc/terms/title",
    "http://purl.org/dc/terms/description",
    "https://grafsync.graphifi.com/schema/vm/associatedProject",
    "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
    "https://grafsync.graphifi.com/schema/gs/activateWorkflow"
  ]
};

class VocabularyProjectInfoContainer extends Component {

  constructor(props) {
    super(props);
    this.state = {
      initialLoad: true,
      isLoading: false,
      infoLoading: true,
      taxonomies: null,
      collections: null,
      ontologyProjects: null,
      projectUsers: undefined,
      ontologyProjectAssociation: null,
      isErrored: undefined,
      entitlements: undefined
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {
    if (props.data._version !== state._version) {
      return {
        infoLoading: true,
        isLoading: false,
        _version: props.data._version,
      };
    }
    return null;
  }

  componentDidMount() {
    this.collectProjectData();
  }

  componentDidUpdate() {
    if (!this.state.isErrored && this.state.infoLoading === true) {
      this.collectProjectData();
    }
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, false));
    return this.cancellable;
  }

  loadModel(graph) {
    this.cancellable = makeCancelable(GS.loadModel(graph));
    return this.cancellable;
  }

  loadEntitlements() {
    this.cancellable = makeCancelable(authorisationService.getProjectEntitlements(this.props.user, GS.getActiveSubscription(), GS.getActiveSubscriptionBucket(), this.props.project));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  doGetUsers() {
    this.cancellable = makeCancelable(GS.subscriptionUsers(this.props.id));
    return this.cancellable;
  }

  collectProjectData() {
    if (this.state.isLoading) return;
    // At some point we want to filter the properties loaded for each component
    this.setState({ isLoading: true }, async () => {
      try {
        // We only load these once - they won't change very often at all
        if (this.state.entitlements === undefined) {
          const entitlements = await this.loadEntitlements().promise;
          if (this.cancellable && this.cancellable.isCancelled()) return;
          const users = await this.doGetUsers().promise;
          if (this.cancellable && this.cancellable.isCancelled()) return;
          const projectUsers = users.map(user => ({
            value: user.id,
            label: user.givenName && user.familyName ?
              user.givenName + " " + user.familyName
              :
              user.username
          })).sort(function (a, b) {
            return a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1;
          });
          this.setState({ entitlements: entitlements.hasEntitlement, projectUsers });
        }
        if (this.props.data.hasComponent || this.props.data.ontologyProjectAssociation) {
          const opa = this.props.data.ontologyProjectAssociation ? this.props.data.ontologyProjectAssociation : [];
          const cpt = this.props.data.hasComponent ? this.props.data.hasComponent : [];
          const resources = [...opa, ...cpt];
          const mergedLoadOptions = Object.assign({}, loadOptions, { language: this.props.data.defaultLang });
          if (resources.length > 0) {
            await this.loadData(resources, mergedLoadOptions).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
          }
          await this.processProjects();
          if (this.cancellable && this.cancellable.isCancelled()) return;
          this.processComponents();
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ isErrored: e });
      }
      this.setState({ isLoading: false, infoLoading: false, initialLoad: false });
    });
  }

  async processProjects() {
    const { ontologyProjectAssociation } = this.props.data;
    if (ontologyProjectAssociation) {
      let ontologyProjects = [];
      const fetchModels = ontologyProjectAssociation.map(opa => {
        const opaData = GS.data(opa, this.props.graph);
        ontologyProjects.push(opaData.associatedProject);
        // We load models so we see updates to it
        return this.loadModel(getGraphFromProject(opaData.associatedProject)).promise;
      });
      await Promise.all(fetchModels);
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ ontologyProjects });
    }
  }

  processComponents() {
    try {
      if (this.props.data.hasComponent) {
        let taxonomies = [];
        let collections = [];
        this.props.data.hasComponent.forEach(component => {
          // We go false here because potentially a user may not have permissions for all components
          // We don't want to error - just not display the components they can't access
          const componentData = GS.data(component, this.props.graph, false);
          if (componentData) {
            if (componentData.type.includes("ConceptScheme")) {
              taxonomies.push({
                id: componentData.id,
                title: componentData.title,
                type: componentData.type,
                useWorkflow: componentData.activateWorkflow === true,
                description: componentData.description
              });
            }
            else if (componentData.type.includes("OrderedCollection") || componentData.type.includes("Collection")) {
              collections.push({
                id: componentData.id,
                title: componentData.title,
                type: componentData.type,
                description: componentData.description
              });
            }
          }
        });
        sortObjects(taxonomies, 'title', this.props.data.defaultLang, null, false);
        sortObjects(collections, 'title', this.props.data.defaultLang, null, false);
        this.setState({
          taxonomies,
          collections
        });
      }
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });

    }
  }

  // Note slash-hash IRIs not applicable at the moment as collections cannot create concepts
  addCollection = async (title, collectionIRI, isSlashIRI = null, description, lang = "en", isOrdered) => {
    let newCollection = {
      id: collectionIRI,
      type: isOrdered ? "OrderedCollection" : "Collection",
      title: { [lang]: title },
      ...(description && description !== "") && { description: { [lang]: description } }
    };
    await GS.insertResource(newCollection, collectionIRI, this.props.graph, [this.props.project]);
    GS.addProperty(this.props.project, this.props.graph, 'hasComponent', collectionIRI);
  };

  addTaxonomy = async (title, schemeIRI, isSlashIRI, description, lang = "en", isOrdered, isUuidIRI, useXL = false) => {
    let newTaxonomy = {
      id: schemeIRI,
      type: "ConceptScheme",
      title: { [lang]: title },
      useSlashIRI: isSlashIRI,
      useUuidIRI: isUuidIRI,
      minimiseEncoding: true,
      useXL,
      ...(description && description !== "") && { description: { [lang]: description } }
    };
    await GS.insertResource(newTaxonomy, schemeIRI, this.props.graph, [this.props.project]);
    GS.addProperty(this.props.project, this.props.graph, 'hasComponent', schemeIRI);
  };

  deleteComponent = (iri, link) => {
    GS.deleteResource(iri, this.props.graph);
    this.props.history.push(link);
  };

  monitorLinkUpdate = (iri, property) => {
    if (property === "title") this.processComponents();
  };

  render() {

    const { id, data, passthroughComponent, graph, lockedGraphs, projectType } = this.props;
    const { isLoading, isErrored, taxonomies, collections, ontologyProjects, entitlements, initialLoad, projectUsers } = this.state;

    if (lockedGraphs && ontologyProjects && lockedGraphs.length > 0 && ontologyProjects.length > 0) {
      const shouldLock = lockedGraphs.some(lg => ontologyProjects.some(op => getGraphFromProject(op) === lg));
      if (shouldLock) return <Locked />;
    }

    if (data.isVersionOfProject) return <VersionWarning />;
    if (projectType && projectType !== data.type) return <Notification text="You are trying to view an incorrect project type." />;
    if (isErrored) return <Errored dataTest="vocabularycontainer-problem" data={isErrored} />;
    if (isLoading && initialLoad) return <Loading offset="40px" size="large" message="Loading project data..." />;
    if (!entitlements) return null;

    const WrappedComponent = passthroughComponent;

    return (
      <ProjectProvider value={data}>
        <WrappedComponent
          {...this.props}
          id={id}
          graph={graph}
          project={id}
          data={data}
          projectUsers={projectUsers}
          skosXL={data.skosXL || false}
          entitlements={entitlements}
          taxonomies={taxonomies}
          collections={collections}
          components={taxonomies}
          ontologyProjects={ontologyProjects}
          addCollection={this.addCollection}
          associatedProjects={ontologyProjects}
          onDeleteComponent={this.deleteComponent}
          monitorLinkUpdate={this.monitorLinkUpdate}
          addTaxonomy={this.addTaxonomy}
        />
      </ProjectProvider>
    );
  }
}

VocabularyProjectInfoContainer.propTypes = {
  data: PropTypes.object.isRequired,
  projectType: PropTypes.string,
  id: PropTypes.string.isRequired,
  lockedGraphs: PropTypes.array,
  project: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  history: PropTypes.object,
  graph: PropTypes.string.isRequired
};

export default withRouter(withGS(VocabularyProjectInfoContainer));